import React, { useState, useEffect, useCallback, useMemo } from 'react';

import store from 'store';

import * as api from 'api/methods';

import {
  fetchAllCoursesForLessonAttach,
  fetchDefaultLessonOrderForCourse,
  fetchLessonOrderOptionsForCourse,
  type CourseLessonRow,
  type LessonOrderOption,
  type LinkedCourse,
} from 'store/courses-lessons/helpers';

import { getCourseType } from 'components/CoursesTable/CoursesTable.helpers';

import LessonAttachCoursesDrawer from './LessonAttachCoursesDrawer';

import {
  fetchModulesForCourse,
  isModularCourse,
  type CourseModuleOption,
} from './LessonAttachCoursesDrawer.helpers';

type CourseWithTypeLabel = LinkedCourse & {
  typeLabel?: string;
};

const areCourseIdSetsEqual = (
  firstCourseIds: Set<string>,
  secondCourseIds: Set<string>,
): boolean => {
  if (firstCourseIds.size !== secondCourseIds.size) {
    return false;
  }

  for (const courseId of firstCourseIds) {
    if (!secondCourseIds.has(courseId)) {
      return false;
    }
  }

  return true;
};

const isValidLessonOrder = (order: number | undefined): boolean =>
  typeof order === 'number' && Number.isInteger(order) && order >= 1;

const LessonAttachCoursesDrawerContainer = ({
  open,
  lesson,
  onClose,
  onAttachmentsChange,
}: {
  open: boolean;
  lesson: CourseLessonRow | null;
  onClose: () => void;
  onAttachmentsChange: () => void | Promise<void>;
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [courses, setCourses] = useState<CourseWithTypeLabel[]>([]);
  const [initialAttachedCourseIds, setInitialAttachedCourseIds] = useState<
    Set<string>
  >(() => new Set());
  const [draftAttachedCourseIds, setDraftAttachedCourseIds] = useState<
    Set<string>
  >(() => new Set());
  const [modulesByCourseId, setModulesByCourseId] = useState<
    Record<string, CourseModuleOption[]>
  >({});
  const [modulesLoadingCourseIds, setModulesLoadingCourseIds] = useState<
    Set<string>
  >(() => new Set());
  const [draftModuleByCourseId, setDraftModuleByCourseId] = useState<
    Record<string, string>
  >({});
  const [draftOrderByCourseId, setDraftOrderByCourseId] = useState<
    Record<string, number>
  >({});
  const [orderOptionsByCourseId, setOrderOptionsByCourseId] = useState<
    Record<string, LessonOrderOption[]>
  >({});

  const resetAttachDraftState = useCallback(() => {
    setModulesByCourseId({});
    setModulesLoadingCourseIds(new Set());
    setDraftModuleByCourseId({});
    setDraftOrderByCourseId({});
    setOrderOptionsByCourseId({});
  }, []);

  const loadDefaultOrderForCourse = useCallback(
    async (courseId: string, moduleId?: string) => {
      try {
        const [defaultOrder, orderOptions] = await Promise.all([
          fetchDefaultLessonOrderForCourse(courseId, moduleId),
          fetchLessonOrderOptionsForCourse(courseId, moduleId),
        ]);

        setDraftOrderByCourseId((previousDraftOrderByCourseId) => ({
          ...previousDraftOrderByCourseId,
          [courseId]: defaultOrder,
        }));

        setOrderOptionsByCourseId((previousOrderOptionsByCourseId) => ({
          ...previousOrderOptionsByCourseId,
          [courseId]: orderOptions,
        }));
      } catch (error) {
        console.warn(error);

        setDraftOrderByCourseId((previousDraftOrderByCourseId) => ({
          ...previousDraftOrderByCourseId,
          [courseId]: 1,
        }));

        setOrderOptionsByCourseId((previousOrderOptionsByCourseId) => ({
          ...previousOrderOptionsByCourseId,
          [courseId]: [{ label: '1', value: '1' }],
        }));
      }
    },
    [],
  );

  const loadModulesForCourse = useCallback(async (course: LinkedCourse) => {
    setModulesLoadingCourseIds((previousLoadingCourseIds) => {
      const nextLoadingCourseIds = new Set(previousLoadingCourseIds);
      nextLoadingCourseIds.add(course.id);
      return nextLoadingCourseIds;
    });

    try {
      const modules = await fetchModulesForCourse(course);

      setModulesByCourseId((previousModulesByCourseId) => ({
        ...previousModulesByCourseId,
        [course.id]: modules,
      }));

      if (modules.length === 1) {
        setDraftModuleByCourseId((previousDraftModuleByCourseId) => ({
          ...previousDraftModuleByCourseId,
          [course.id]: modules[0].id,
        }));

        void loadDefaultOrderForCourse(course.id, modules[0].id);
      }
    } catch (error) {
      console.warn(error);
      setModulesByCourseId((previousModulesByCourseId) => ({
        ...previousModulesByCourseId,
        [course.id]: [],
      }));
    }

    setModulesLoadingCourseIds((previousLoadingCourseIds) => {
      const nextLoadingCourseIds = new Set(previousLoadingCourseIds);
      nextLoadingCourseIds.delete(course.id);
      return nextLoadingCourseIds;
    });
  }, [loadDefaultOrderForCourse]);

  useEffect(() => {
    if (!open || !lesson) {
      return;
    }

    const lessonAttachedCourseIds = new Set(
      lesson.courses.map((course) => course.id),
    );

    setInitialAttachedCourseIds(lessonAttachedCourseIds);
    setDraftAttachedCourseIds(new Set(lessonAttachedCourseIds));
    resetAttachDraftState();

    const loadCourses = async () => {
      setLoading(true);

      try {
        const allCourses = await fetchAllCoursesForLessonAttach(
          store.getState,
        );

        setCourses(
          allCourses.map((course) => ({
            ...course,
            typeLabel: getCourseType(course.type),
          })),
        );
      } catch (error) {
        console.warn(error);
        setCourses([]);
      }

      setLoading(false);
    };

    loadCourses();
  }, [lesson, open, resetAttachDraftState]);

  const hasChanges = useMemo(
    () =>
      !areCourseIdSetsEqual(initialAttachedCourseIds, draftAttachedCourseIds),
    [draftAttachedCourseIds, initialAttachedCourseIds],
  );

  const hasMissingModuleForNewModularAttach = useMemo(() => {
    for (const courseId of draftAttachedCourseIds) {
      if (initialAttachedCourseIds.has(courseId)) {
        continue;
      }

      const course = courses.find((listedCourse) => listedCourse.id === courseId);

      if (course && isModularCourse(course) && !draftModuleByCourseId[courseId]) {
        return true;
      }
    }

    return false;
  }, [
    courses,
    draftAttachedCourseIds,
    draftModuleByCourseId,
    initialAttachedCourseIds,
  ]);

  const hasInvalidOrderForNewAttach = useMemo(() => {
    for (const courseId of draftAttachedCourseIds) {
      if (initialAttachedCourseIds.has(courseId)) {
        continue;
      }

      const course = courses.find((listedCourse) => listedCourse.id === courseId);

      if (course && isModularCourse(course) && !draftModuleByCourseId[courseId]) {
        continue;
      }

      if (!isValidLessonOrder(draftOrderByCourseId[courseId])) {
        return true;
      }
    }

    return false;
  }, [
    courses,
    draftAttachedCourseIds,
    draftModuleByCourseId,
    draftOrderByCourseId,
    initialAttachedCourseIds,
  ]);

  const canSave =
    hasChanges &&
    !hasMissingModuleForNewModularAttach &&
    !hasInvalidOrderForNewAttach;

  const handleCourseToggle = useCallback(
    (course: LinkedCourse, isAttached: boolean) => {
      setDraftAttachedCourseIds((previousDraftAttachedCourseIds) => {
        const nextDraftAttachedCourseIds = new Set(
          previousDraftAttachedCourseIds,
        );

        if (isAttached) {
          nextDraftAttachedCourseIds.delete(course.id);
        } else {
          nextDraftAttachedCourseIds.add(course.id);
        }

        return nextDraftAttachedCourseIds;
      });

      if (isAttached) {
        setDraftModuleByCourseId((previousDraftModuleByCourseId) => {
          const nextDraftModuleByCourseId = { ...previousDraftModuleByCourseId };
          delete nextDraftModuleByCourseId[course.id];
          return nextDraftModuleByCourseId;
        });

        setDraftOrderByCourseId((previousDraftOrderByCourseId) => {
          const nextDraftOrderByCourseId = { ...previousDraftOrderByCourseId };
          delete nextDraftOrderByCourseId[course.id];
          return nextDraftOrderByCourseId;
        });

        setOrderOptionsByCourseId((previousOrderOptionsByCourseId) => {
          const nextOrderOptionsByCourseId = { ...previousOrderOptionsByCourseId };
          delete nextOrderOptionsByCourseId[course.id];
          return nextOrderOptionsByCourseId;
        });

        return;
      }

      if (!isModularCourse(course)) {
        void loadDefaultOrderForCourse(course.id);
        return;
      }

      const cachedModules = modulesByCourseId[course.id];

      if (cachedModules?.length === 1) {
        setDraftModuleByCourseId((previousDraftModuleByCourseId) => ({
          ...previousDraftModuleByCourseId,
          [course.id]: cachedModules[0].id,
        }));
        void loadDefaultOrderForCourse(course.id, cachedModules[0].id);
        return;
      }

      if (!cachedModules && !modulesLoadingCourseIds.has(course.id)) {
        void loadModulesForCourse(course);
      }
    },
    [
      loadDefaultOrderForCourse,
      loadModulesForCourse,
      modulesByCourseId,
      modulesLoadingCourseIds,
    ],
  );

  const handleModuleChange = useCallback(
    (courseId: string, moduleId: string) => {
      setDraftModuleByCourseId((previousDraftModuleByCourseId) => ({
        ...previousDraftModuleByCourseId,
        [courseId]: moduleId,
      }));

      if (
        draftAttachedCourseIds.has(courseId) &&
        !initialAttachedCourseIds.has(courseId)
      ) {
        void loadDefaultOrderForCourse(courseId, moduleId);
      }
    },
    [
      draftAttachedCourseIds,
      initialAttachedCourseIds,
      loadDefaultOrderForCourse,
    ],
  );

  const handleOrderChange = useCallback((courseId: string, order: number) => {
    setDraftOrderByCourseId((previousDraftOrderByCourseId) => ({
      ...previousDraftOrderByCourseId,
      [courseId]: order,
    }));
  }, []);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async () => {
    if (!lesson || !canSave || saving) {
      return;
    }

    const toAttach = [...draftAttachedCourseIds].filter(
      (courseId) => !initialAttachedCourseIds.has(courseId),
    );
    const toDetach = [...initialAttachedCourseIds].filter(
      (courseId) => !draftAttachedCourseIds.has(courseId),
    );

    setSaving(true);

    try {
      await Promise.all([
        ...toAttach.map((courseId) => {
          const course = courses.find((listedCourse) => listedCourse.id === courseId);
          const moduleId =
            course && isModularCourse(course)
              ? draftModuleByCourseId[courseId]
              : undefined;

          return api.linkLessonToCourse({
            courseId,
            lessonId: lesson.id,
            moduleId,
            order: draftOrderByCourseId[courseId],
          });
        }),
        ...toDetach.map((courseId) =>
          api.removeCourseLessonById({
            courseId,
            lessonId: lesson.id,
          }),
        ),
      ]);

      await onAttachmentsChange();
      onClose();
    } catch (error) {
      console.warn(error);
    }

    setSaving(false);
  }, [
    canSave,
    courses,
    draftAttachedCourseIds,
    draftModuleByCourseId,
    draftOrderByCourseId,
    initialAttachedCourseIds,
    lesson,
    onAttachmentsChange,
    onClose,
    saving,
  ]);

  if (!lesson) {
    return null;
  }

  return (
    <LessonAttachCoursesDrawer
      open={open}
      lessonTitle={lesson.title}
      loading={loading}
      saving={saving}
      canSave={canSave}
      courses={courses}
      attachedCourseIds={draftAttachedCourseIds}
      initialAttachedCourseIds={initialAttachedCourseIds}
      modulesByCourseId={modulesByCourseId}
      modulesLoadingCourseIds={modulesLoadingCourseIds}
      draftModuleByCourseId={draftModuleByCourseId}
      draftOrderByCourseId={draftOrderByCourseId}
      orderOptionsByCourseId={orderOptionsByCourseId}
      onClose={handleCancel}
      onCancel={handleCancel}
      onSave={handleSave}
      onCourseToggle={handleCourseToggle}
      onModuleChange={handleModuleChange}
      onOrderChange={handleOrderChange}
    />
  );
};

export default React.memo(LessonAttachCoursesDrawerContainer) as any;
